import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import ExpectedAttendeePicker from '../components/ExpectedAttendeePicker';
import CoHostPicker from '../components/CoHostPicker';
import Modal from '../components/Modal';
import RoomAvailabilityPicker from '../components/RoomAvailabilityPicker';
import ChairmanPicker from '../components/ChairmanPicker';
import { createMyMeetingSeries, getRoomCalendar, getRoomCalendarRange, RoomBookingConflictError } from '../lib/api';
import {
  clampReservations,
  copyFirstDayRooms,
  daysInclusive,
  END_TIME_OPTIONS,
  generateOccurrences,
  isIsoDate,
  minutesFromTime,
  occurrenceIntervalError,
  reservationIntervalError,
  START_TIME_OPTIONS,
} from '../lib/meetingSeries';
import { bookingOverlapsMinutes } from '../lib/roomCalendar';
import { dedupeAttendeeValues, errorMessage, formatDate, formatDateWithWeekday, formatTime, formatTimeRange, generateSecret, getMalaysiaTodayISO } from '../lib/utils';
import { storage } from '../lib/storage';
import { useToast } from '../context/ToastContext';
import { useStaffAuth } from '../auth/StaffAuthContext';
import type { MeetingLocationType, MeetingOccurrenceInput, MeetingRoomReservationInput, RoomCalendarRoom } from '../types';

const OFFICE_START = '07:30';
const OFFICE_END = '18:00';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function reservationOptions(occurrence: MeetingOccurrenceInput, endOptions: boolean): string[] {
  return (endOptions ? END_TIME_OPTIONS : START_TIME_OPTIONS).filter((time) => endOptions ? time > OFFICE_START : time < OFFICE_END);
}

function syncMeetingTime(occurrence: MeetingOccurrenceInput, startTime: string, endTime: string): MeetingOccurrenceInput {
  return {
    ...occurrence,
    startTime,
    endTime,
    rooms: occurrence.rooms.map((room) => room.useMeetingTime === false ? room : { ...room, startTime, endTime }),
  };
}

function syncReservationMeetingTimes(occurrence: MeetingOccurrenceInput): MeetingOccurrenceInput {
  return syncMeetingTime(occurrence, occurrence.startTime, occurrence.endTime);
}

function roomName(rooms: RoomCalendarRoom[], roomId: string | null): string {
  if (!roomId) return 'Bilik belum dipilih';
  return rooms.find((room) => room.room_id === roomId)?.name ?? 'Bilik dipilih';
}

export default function CreateMeetingPage() {
  const { identity } = useStaffAuth();
  const [searchParams] = useSearchParams();
  const today = getMalaysiaTodayISO();
  const queryDate = searchParams.get('date') ?? '';
  const queryStart = searchParams.get('start') ?? '';
  const queryRoom = searchParams.get('room') ?? '';
  const prefillDate = isIsoDate(queryDate) && queryDate >= today ? queryDate : '';
  const prefillStartMinutes = minutesFromTime(queryStart);
  const prefillStart = prefillStartMinutes !== null && prefillStartMinutes >= 450 && prefillStartMinutes < 1080 ? queryStart : '';
  const prefillRoom = UUID_PATTERN.test(queryRoom) ? queryRoom : '';
  const [draft] = useState(() => storage.getCreateMeetingDraft(identity.subject));
  const [title, setTitle] = useState(() => draft?.title ?? '');
  const [startDate, setStartDate] = useState(() => prefillDate || draft?.startDate || '');
  const [endDate, setEndDate] = useState(() => prefillDate || draft?.endDate || '');
  const [fullDay, setFullDay] = useState(() => prefillStart ? false : draft?.fullDay ?? false);
  const [sameTimeEveryDay, setSameTimeEveryDay] = useState(() => draft?.sameTimeEveryDay ?? false);
  const [startTime, setStartTime] = useState(() => prefillStart || (draft?.fullDay ? OFFICE_START : draft?.startTime ?? ''));
  const [endTime, setEndTime] = useState(() => prefillStart ? '' : draft?.fullDay ? OFFICE_END : draft?.endTime ?? '');
  const [locationType, setLocationType] = useState<MeetingLocationType>(() => prefillRoom ? 'MTIB_ROOM' : draft?.locationType ?? 'MTIB_ROOM');
  const [externalLocation, setExternalLocation] = useState(() => draft?.externalLocation ?? '');
  const [occurrences, setOccurrences] = useState<MeetingOccurrenceInput[]>(() => prefillDate ? [] : draft?.dailySchedule ?? []);
  const [excludedDates, setExcludedDates] = useState<string[]>(() => draft?.excludedDates ?? []);
  const [pendingPrefillRoom, setPendingPrefillRoom] = useState(prefillRoom);
  const [rooms, setRooms] = useState<RoomCalendarRoom[]>([]);
  const [availabilityStatus, setAvailabilityStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [availabilityRefresh, setAvailabilityRefresh] = useState(0);
  const [roomPickerTarget, setRoomPickerTarget] = useState<{ occurrenceDate: string; clientId: string } | null>(null);
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);
  const [chairman, setChairman] = useState(() => draft?.chairman ?? '');
  const [expected, setExpected] = useState<string[]>(() => draft?.expected ?? []);
  const [cohostEmails, setCohostEmails] = useState<string[]>(() => draft?.cohosts ?? []);
  const [busy, setBusy] = useState(false);
  const [resetConfirmationOpen, setResetConfirmationOpen] = useState(false);
  const scheduleInitialized = useRef(false);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const dayCount = daysInclusive(startDate, endDate);
  const scheduleValid = dayCount !== null && occurrences.length > 0 && occurrences.length <= dayCount
    && new Set(occurrences.map((occurrence) => occurrence.date)).size === occurrences.length
    && occurrences.every((occurrence) => occurrenceIntervalError(occurrence, today) === null);
  const missingRoomDates = useMemo(() => locationType === 'MTIB_ROOM'
    ? occurrences.filter((occurrence) => occurrence.rooms.every((room) => !room.roomId)).map((occurrence) => occurrence.date)
    : [], [locationType, occurrences]);

  useEffect(() => {
    scheduleInitialized.current = true;
    const excluded = new Set(excludedDates);
    setOccurrences((previous) => generateOccurrences({ startDate, endDate, startTime, endTime, fullDay, previous })
      .filter((occurrence) => !excluded.has(occurrence.date)));
  }, [endDate, excludedDates, fullDay, startDate]);

  useEffect(() => {
    if (!pendingPrefillRoom || !occurrences.length || !endTime) return;
    setOccurrences((current) => current.map((occurrence, index) => index === 0 && !occurrence.rooms.length ? {
      ...occurrence,
      rooms: [{ clientId: crypto.randomUUID(), roomId: pendingPrefillRoom, startTime: occurrence.startTime, endTime: occurrence.endTime }],
    } : occurrence));
    setPendingPrefillRoom('');
  }, [endTime, occurrences.length, pendingPrefillRoom]);

  useEffect(() => {
    const empty = !title && !startDate && !endDate && !startTime && !endTime && !fullDay
      && locationType === 'MTIB_ROOM' && !externalLocation && !chairman && expected.length === 0
      && cohostEmails.length === 0 && excludedDates.length === 0 && occurrences.length === 0;
    if (empty) {
      storage.clearCreateMeetingDraft();
      return;
    }
    storage.setCreateMeetingDraft(identity.subject, {
      title, startDate, endDate, startTime, endTime, fullDay, sameTimeEveryDay, locationType,
      externalLocation, chairman, expected, cohosts: cohostEmails, excludedDates, dailySchedule: occurrences,
    });
  }, [chairman, cohostEmails, endDate, endTime, excludedDates, expected, externalLocation, fullDay, identity.subject, locationType, occurrences, sameTimeEveryDay, startDate, startTime, title]);

  useEffect(() => {
    let cancelled = false;
    if (locationType !== 'MTIB_ROOM' || !scheduleValid || !dayCount) {
      setRooms([]);
      setAvailabilityStatus('idle');
      return () => { cancelled = true; };
    }
    setAvailabilityStatus('loading');
    const calendarRequest = dayCount === 1
      ? getRoomCalendar({ startDate })
      : getRoomCalendarRange({ startDate, dayCount });
    void calendarRequest.then((calendarRooms) => {
      if (!cancelled) {
        setRooms(calendarRooms);
        setAvailabilityStatus('ready');
      }
    }).catch(() => {
      if (!cancelled) {
        setRooms([]);
        setAvailabilityStatus('error');
      }
    });
    return () => { cancelled = true; };
  }, [availabilityRefresh, dayCount, locationType, scheduleValid, startDate]);

  useEffect(() => {
    const refreshOnFocus = () => setAvailabilityRefresh((current) => current + 1);
    window.addEventListener('focus', refreshOnFocus);
    return () => window.removeEventListener('focus', refreshOnFocus);
  }, []);

  const selectedPickerReservation = useMemo(() => {
    if (!roomPickerTarget) return null;
    const occurrence = occurrences.find((item) => item.date === roomPickerTarget.occurrenceDate);
    const reservation = occurrence?.rooms.find((item) => item.clientId === roomPickerTarget.clientId);
    return occurrence && reservation ? { occurrence, reservation } : null;
  }, [occurrences, roomPickerTarget]);

  const updateOccurrence = (index: number, patch: Partial<MeetingOccurrenceInput>) => {
    setOccurrences((current) => current.map((occurrence, occurrenceIndex) => (
      occurrenceIndex === index ? syncReservationMeetingTimes(clampReservations({ ...occurrence, ...patch })) : occurrence
    )));
    setConflictMessage(null);
  };

  const updateReservation = (occurrenceDate: string, clientId: string, patch: Partial<MeetingRoomReservationInput>) => {
    setOccurrences((current) => current.map((occurrence) => occurrence.date === occurrenceDate ? {
      ...occurrence,
      rooms: occurrence.rooms.map((room) => room.clientId === clientId ? {
        ...room, ...patch,
        ...(patch.useMeetingTime === true ? { startTime: occurrence.startTime, endTime: occurrence.endTime } : {}),
      } : room),
    } : occurrence));
    setConflictMessage(null);
  };

  const addRoom = (occurrenceDate: string) => {
    const clientId = crypto.randomUUID();
    setOccurrences((current) => current.map((occurrence) => occurrence.date === occurrenceDate ? {
      ...occurrence,
      rooms: [...occurrence.rooms, { clientId, roomId: null, startTime: occurrence.startTime, endTime: occurrence.endTime, useMeetingTime: true }],
    } : occurrence));
    setRoomPickerTarget({ occurrenceDate, clientId });
  };

  const removeRoom = (occurrenceDate: string, clientId: string) => {
    setOccurrences((current) => current.map((occurrence) => occurrence.date === occurrenceDate ? {
      ...occurrence,
      rooms: occurrence.rooms.filter((room) => room.clientId !== clientId),
    } : occurrence));
    setRoomPickerTarget((current) => current?.occurrenceDate === occurrenceDate && current.clientId === clientId ? null : current);
  };

  const changeStartTime = (value: string) => {
    setStartTime(value);
    setOccurrences((current) => current.map((occurrence, index) => sameTimeEveryDay
      ? syncMeetingTime(occurrence, value, endTime)
      : index === 0 ? syncReservationMeetingTimes({ ...occurrence, startTime: value }) : occurrence));
  };

  const changeEndTime = (value: string) => {
    setEndTime(value);
    setOccurrences((current) => current.map((occurrence, index) => sameTimeEveryDay
      ? syncMeetingTime(occurrence, startTime, value)
      : index === current.length - 1 ? syncReservationMeetingTimes({ ...occurrence, endTime: value }) : occurrence));
  };

  const previousNonFullDayTimes = useRef({ start: startTime, end: endTime });
  const changeFullDay = (enabled: boolean) => {
    if (enabled && !fullDay) previousNonFullDayTimes.current = { start: startTime, end: endTime };
    setFullDay(enabled);
    if (enabled) {
      setStartTime(OFFICE_START);
      setEndTime(OFFICE_END);
      setOccurrences((current) => current.map((occurrence) => syncMeetingTime(occurrence, OFFICE_START, OFFICE_END)));
    } else {
      const restoredStart = previousNonFullDayTimes.current.start || '09:00';
      const restoredEnd = previousNonFullDayTimes.current.end || '16:30';
      setStartTime(restoredStart);
      setEndTime(restoredEnd);
      if (sameTimeEveryDay) setOccurrences((current) => current.map((occurrence) => syncMeetingTime(occurrence, restoredStart, restoredEnd)));
    }
  };

  const changeSameTimeEveryDay = (enabled: boolean) => {
    setSameTimeEveryDay(enabled);
    if (enabled && !fullDay && startTime && endTime) setOccurrences((current) => current.map((occurrence) => syncMeetingTime(occurrence, startTime, endTime)));
  };

  const removeDate = (date: string) => {
    if (occurrences.length <= 1) return showToast('Sekurang-kurangnya satu tarikh mesyuarat mesti dikekalkan.', 'error');
    setExcludedDates((current) => [...new Set([...current, date])].sort());
    setOccurrences((current) => current.filter((occurrence) => occurrence.date !== date));
  };

  const restoreDate = (date: string) => setExcludedDates((current) => current.filter((item) => item !== date));

  const hasMeetingInput = Boolean(
    title || startDate || endDate || startTime || endTime || fullDay
    || locationType === 'EXTERNAL' || externalLocation || occurrences.length
    || pendingPrefillRoom || chairman || expected.length || cohostEmails.length || excludedDates.length,
  );

  const resetForm = () => {
    setTitle('');
    setStartDate('');
    setEndDate('');
    setStartTime('');
    setEndTime('');
    setFullDay(false);
    setSameTimeEveryDay(false);
    setLocationType('MTIB_ROOM');
    setExternalLocation('');
    setOccurrences([]);
    setExcludedDates([]);
    setPendingPrefillRoom('');
    setRooms([]);
    setAvailabilityStatus('idle');
    setRoomPickerTarget(null);
    setConflictMessage(null);
    setChairman('');
    setExpected([]);
    setCohostEmails([]);
    setResetConfirmationOpen(false);
    storage.clearCreateMeetingDraft();
    navigate('/meetings/new', { replace: true });
  };

  const requestReset = () => {
    if (hasMeetingInput) setResetConfirmationOpen(true);
    else resetForm();
  };

  const frontendConflict = (): string | null => {
    if (missingRoomDates.length) return `Sila pilih sekurang-kurangnya satu bilik mesyuarat bagi ${missingRoomDates.map(formatDate).join(', ')}.`;
    for (const occurrence of occurrences) {
      for (const reservation of occurrence.rooms) {
        const validation = reservationIntervalError(reservation, occurrence);
        if (validation) return `${formatDate(occurrence.date)}: ${validation}`;
        const duplicate = occurrence.rooms.find((other) => other.clientId !== reservation.clientId
          && other.roomId === reservation.roomId
          && minutesFromTime(other.startTime)! < minutesFromTime(reservation.endTime)!
          && minutesFromTime(other.endTime)! > minutesFromTime(reservation.startTime)!);
        if (duplicate) return `${formatDate(occurrence.date)}: bilik yang sama tidak boleh mempunyai tempahan yang bertindih.`;
        const room = rooms.find((candidate) => candidate.room_id === reservation.roomId);
        if (!room) return `${formatDate(occurrence.date)}: bilik yang dipilih tidak lagi aktif.`;
        if (room && room.bookings.some((booking) => bookingOverlapsMinutes(
          booking, occurrence.date, minutesFromTime(reservation.startTime)!, minutesFromTime(reservation.endTime)!,
        ))) return `${room.name} telah ditempah pada waktu yang dipilih pada ${formatDate(occurrence.date)}.`;
      }
    }
    return null;
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (title.trim().length < 3) return showToast('Sila masukkan tajuk mesyuarat sekurang-kurangnya 3 aksara.', 'error');
    if (!startDate || !endDate || !startTime || !endTime) return showToast('Sila lengkapkan tarikh mula, tarikh tamat, waktu mula dan waktu tamat.', 'error');
    if (!scheduleValid) return showToast('Sila semak tarikh dan jadual setiap hari.', 'error');
    if (locationType === 'EXTERNAL' && !externalLocation.trim()) return showToast('Sila masukkan tempat atau lokasi luar MTIB.', 'error');
    if (locationType === 'MTIB_ROOM' && availabilityStatus !== 'ready') return showToast('Sila tunggu sehingga jadual bilik selesai dimuatkan.', 'error');
    const validation = locationType === 'MTIB_ROOM' ? frontendConflict() : null;
    if (validation) {
      setConflictMessage(validation);
      return showToast(validation, 'error');
    }

    const securedOccurrences = occurrences.map((occurrence) => ({
      ...occurrence,
      hostSecret: generateSecret(),
      projectorSecret: generateSecret(),
      rooms: occurrence.rooms.filter((room): room is MeetingRoomReservationInput & { roomId: string } => Boolean(room.roomId)),
    }));
    setBusy(true);
    try {
      const result = await createMyMeetingSeries({
        title: title.trim(), startDate, endDate, locationType, externalLocation: externalLocation.trim(), chairman: chairman.trim(), expected: dedupeAttendeeValues(expected), cohostEmails: dedupeAttendeeValues(cohostEmails),
        occurrences: securedOccurrences,
      });
      result.occurrences.forEach((created, index) => {
        const secrets = securedOccurrences[index];
        if (secrets) {
          storage.setHostSecret(created.meeting_id, secrets.hostSecret);
          storage.setProjectorSecret(created.meeting_id, secrets.projectorSecret);
        }
      });
      storage.clearCreateMeetingDraft();
      showToast(result.occurrences.length === 1 ? 'Mesyuarat berjaya dicipta.' : `Siri mesyuarat ${result.occurrences.length} hari berjaya dicipta.`, 'success');
      navigate(`/meetings/${result.occurrences[0].meeting_id}`, { replace: true });
    } catch (requestError) {
      if (requestError instanceof RoomBookingConflictError) {
        const detail = requestError.detail;
        const message = `${detail.room_name} tidak lagi tersedia pada ${formatDate(detail.date)}, ${formatTimeRange(detail.start_time, detail.end_time)}.`;
        setConflictMessage(message);
        setAvailabilityRefresh((current) => current + 1);
        showToast(message, 'error');
      } else showToast(errorMessage(requestError), 'error');
    } finally {
      setBusy(false);
    }
  };

  return <><Layout title="Mesyuarat Baharu"><section className="form-card"><form onSubmit={submit} className="form-stack">
    <fieldset className="form-section"><legend>Butiran mesyuarat</legend>
        <label className="field"><span>Tajuk mesyuarat</span><input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={180} required /></label>
      <div className="meeting-time-grid multi-day-range">
        <label className="field"><span>Tarikh mula</span><input type="date" min={today} value={startDate} onChange={(event) => { setStartDate(event.target.value); if (!endDate || event.target.value > endDate) setEndDate(event.target.value); }} required /></label>
        <label className="field"><span>Tarikh tamat</span><input type="date" min={startDate || today} value={endDate} onChange={(event) => setEndDate(event.target.value)} required /></label>
        <label className="field"><span>Waktu mula</span><select value={startTime} onChange={(event) => changeStartTime(event.target.value)} disabled={fullDay} required><option value="">Pilih waktu</option>{START_TIME_OPTIONS.map((time) => <option key={time} value={time}>{formatTime(time)}</option>)}</select></label>
        <label className="field"><span>Waktu tamat</span><select value={endTime} onChange={(event) => changeEndTime(event.target.value)} disabled={fullDay} required><option value="">Pilih waktu</option>{END_TIME_OPTIONS.map((time) => <option key={time} value={time}>{formatTime(time)}</option>)}</select></label>
        <div className="meeting-time-options">
          <label className="full-day-field"><input type="checkbox" checked={fullDay} onChange={(event) => changeFullDay(event.target.checked)} /><span>Sehari penuh</span></label>
          <label className="same-time-field"><input type="checkbox" checked={sameTimeEveryDay} onChange={(event) => changeSameTimeEveryDay(event.target.checked)} disabled={fullDay} /><span>Waktu sama setiap hari</span></label>
        </div>
      </div>
      {startDate && endDate && !dayCount ? <p className="form-validation" role="alert">Pilih julat 1–31 hari kalendar berturutan.</p> : null}
    </fieldset>

    <fieldset className="form-section"><legend>Lokasi</legend>
      <div className="location-type-options" role="radiogroup" aria-label="Jenis lokasi">
        <label className={`location-type-option${locationType === 'MTIB_ROOM' ? ' selected' : ''}`}><input type="radio" name="locationType" checked={locationType === 'MTIB_ROOM'} onChange={() => { setLocationType('MTIB_ROOM'); setExternalLocation(''); }} /><span><strong>Bilik Mesyuarat MTIB</strong><small>Setiap tarikh mesyuarat mesti mempunyai sekurang-kurangnya satu bilik.</small></span></label>
        <label className={`location-type-option${locationType === 'EXTERNAL' ? ' selected' : ''}`}><input type="radio" name="locationType" checked={locationType === 'EXTERNAL'} onChange={() => { setLocationType('EXTERNAL'); setRoomPickerTarget(null); }} /><span><strong>Lokasi Luar MTIB</strong><small>Masukkan tempat mesyuarat atau lokasi dalam talian.</small></span></label>
      </div>
      {locationType === 'EXTERNAL' ? <label className="field"><span>Tempat / lokasi</span><input value={externalLocation} onChange={(event) => setExternalLocation(event.target.value)} maxLength={240} placeholder="Contoh: Putrajaya / Microsoft Teams" required /></label> : null}
    </fieldset>

    {occurrences.length ? <fieldset className="form-section"><div className="daily-schedule-heading"><legend>Jadual harian</legend>{occurrences.length > 1 && locationType === 'MTIB_ROOM' ? <button type="button" className="button button-ghost button-small" onClick={() => setOccurrences((current) => copyFirstDayRooms(current))} disabled={!occurrences[0].rooms.length}>Guna bilik Hari 1 untuk semua hari</button> : null}</div>
      <div className="daily-schedule-list">
        {occurrences.map((occurrence, occurrenceIndex) => {
          const occurrenceError = occurrenceIntervalError(occurrence, today);
          return <article className="daily-schedule-card" key={occurrence.date}>
            <div className="daily-schedule-head"><div><strong>{formatDateWithWeekday(occurrence.date)}</strong><span>Hari {occurrenceIndex + 1} daripada {occurrences.length}</span></div><div className="daily-time-controls">
              <select aria-label={`Waktu mula ${formatDateWithWeekday(occurrence.date)}`} value={occurrence.startTime} onChange={(event) => updateOccurrence(occurrenceIndex, { startTime: event.target.value })} disabled={sameTimeEveryDay}>{START_TIME_OPTIONS.map((time) => <option key={time} value={time}>{formatTime(time)}</option>)}</select>
              <span>hingga</span>
              <select aria-label={`Waktu tamat ${formatDateWithWeekday(occurrence.date)}`} value={occurrence.endTime} onChange={(event) => updateOccurrence(occurrenceIndex, { endTime: event.target.value })} disabled={sameTimeEveryDay}>{END_TIME_OPTIONS.map((time) => <option key={time} value={time}>{formatTime(time)}</option>)}</select>
              <button type="button" className="button button-ghost button-small" onClick={() => removeDate(occurrence.date)} disabled={occurrences.length <= 1} aria-label={`Keluarkan ${formatDateWithWeekday(occurrence.date)}`}>Keluarkan hari</button>
            </div></div>
            {occurrenceError ? <p className="form-validation" role="alert">{occurrenceError}</p> : null}
            {locationType === 'MTIB_ROOM' ? <div className="daily-room-assignments">
              {occurrence.rooms.map((reservation) => {
                const reservationError = reservationIntervalError(reservation, occurrence);
                return <div className="daily-room-assignment" key={reservation.clientId}>
                  <button
                    type="button"
                    className={`room-assignment-name${reservation.roomId ? '' : ' unselected'}`}
                    onClick={() => setRoomPickerTarget({ occurrenceDate: occurrence.date, clientId: reservation.clientId })}
                    aria-label={`${reservation.roomId ? 'Tukar' : 'Pilih'} bilik untuk ${formatDate(occurrence.date)}`}
                  ><span className="room-assignment-label">Bilik</span><strong>{reservation.roomId ? roomName(rooms, reservation.roomId) : 'Pilih bilik mesyuarat'}</strong><span className="room-assignment-chevron" aria-hidden="true">›</span></button>
                  <div className="room-reservation-heading"><strong>Waktu tempahan bilik</strong><label className="same-time-field"><input type="checkbox" checked={reservation.useMeetingTime !== false} onChange={(event) => updateReservation(occurrence.date, reservation.clientId, { useMeetingTime: event.target.checked })} /><span>Guna waktu mesyuarat</span></label></div>
                  <label><span>Tempah dari</span><select value={reservation.startTime} onChange={(event) => updateReservation(occurrence.date, reservation.clientId, { startTime: event.target.value, useMeetingTime: false })} disabled={reservation.useMeetingTime !== false}>{reservationOptions(occurrence, false).map((time) => <option key={time} value={time}>{formatTime(time)}</option>)}</select></label>
                  <label><span>Tempah hingga</span><select value={reservation.endTime} onChange={(event) => updateReservation(occurrence.date, reservation.clientId, { endTime: event.target.value, useMeetingTime: false })} disabled={reservation.useMeetingTime !== false}>{reservationOptions(occurrence, true).map((time) => <option key={time} value={time}>{formatTime(time)}</option>)}</select></label>
                  <button type="button" className="button button-ghost button-small" onClick={() => removeRoom(occurrence.date, reservation.clientId)} aria-label={`Keluarkan ${roomName(rooms, reservation.roomId)}`}>Keluarkan</button>
                  {reservationError ? <p className="form-validation">{reservationError}</p> : null}
                </div>;
              })}
              <button type="button" className="button button-secondary button-small add-room-button" onClick={() => addRoom(occurrence.date)}>+ Tambah bilik lain</button>
            </div> : null}
          </article>;
        })}
      </div>
      {excludedDates.length > 0 ? <div className="excluded-dates"><strong>Tarikh dikecualikan</strong>{excludedDates.map((date) => <div key={date}><span>{formatDateWithWeekday(date)}</span><button type="button" className="button button-ghost button-small" onClick={() => restoreDate(date)} aria-label={`Pulihkan ${formatDateWithWeekday(date)}`}>Pulihkan</button></div>)}</div> : null}
      {missingRoomDates.length ? <p className="form-validation conflict-summary" role="alert">Sila pilih sekurang-kurangnya satu bilik mesyuarat bagi {missingRoomDates.map(formatDate).join(', ')}.</p> : null}
      {conflictMessage ? <p className="form-validation conflict-summary" role="alert">{conflictMessage}</p> : null}
    </fieldset> : null}

    {locationType === 'MTIB_ROOM' && selectedPickerReservation ? <RoomAvailabilityPicker
      rooms={rooms}
      occurrences={occurrences}
      target={{
        date: selectedPickerReservation.occurrence.date,
        startTime: selectedPickerReservation.reservation.startTime,
        endTime: selectedPickerReservation.reservation.endTime,
      }}
      selectedRoomId={selectedPickerReservation.reservation.roomId}
      status={availabilityStatus}
      onSelect={(roomId) => {
        const { occurrence, reservation } = selectedPickerReservation;
        updateReservation(occurrence.date, reservation.clientId, reservation.roomId ? { roomId } : {
          roomId,
          startTime: occurrence.startTime,
          endTime: occurrence.endTime,
          useMeetingTime: true,
        });
        setRoomPickerTarget(null);
      }}
      onClose={() => setRoomPickerTarget(null)}
      onRetry={() => setAvailabilityRefresh((current) => current + 1)}
    /> : null}

    <fieldset className="form-section"><legend>PESERTA MESYUARAT</legend>
      <label className="field"><span>Pengerusi <em>(Pilihan)</em></span><ChairmanPicker value={chairman} onChange={setChairman} expected={expected} onExpectedChange={setExpected} /></label>
      <label className="field"><span>Ahli Dijemput <em>(Pilihan)</em></span><ExpectedAttendeePicker values={expected} autoValues={cohostEmails} onChange={setExpected} /></label>
      <label className="field"><span><em>Co-Host</em> <em>(Pilihan)</em></span><CoHostPicker values={cohostEmails} onChange={setCohostEmails} hostEmail={identity.email ?? ''} /><small className="muted"><em>Co-Host</em> akan dimasukkan secara automatik sebagai ahli dijemput.</small></label>
    </fieldset>

    <div className="form-actions"><button type="button" className="button button-ghost" onClick={() => navigate('/dashboard')}>Batal</button><button type="button" className="button button-secondary" onClick={requestReset} disabled={busy}>Set Semula</button><button className="button button-primary" disabled={busy}>{busy ? 'Sedang mencipta…' : occurrences.length > 1 ? `Cipta siri ${occurrences.length} hari` : 'Cipta mesyuarat'}</button></div>
  </form></section></Layout>;

  {resetConfirmationOpen ? <Modal title="Set semula borang mesyuarat?" onClose={() => setResetConfirmationOpen(false)}>
    <p>Butiran mesyuarat yang telah dimasukkan akan dipadam.</p>
    <div className="modal-actions"><button type="button" className="button button-ghost" onClick={() => setResetConfirmationOpen(false)}>Teruskan suntingan</button><button type="button" className="button button-danger" onClick={resetForm}>Set Semula</button></div>
  </Modal> : null}
  </>;
}
